import type { PlacedPart } from './assembly'
import type { AssemblyConnection } from './connection'

export const PINGPONG_CUBE_PART_ID = 'part-cube-stl-step'
export const MD01_PART_ID = 'part-link-md-md-01'
// C/D and D/E each pair a 20-tooth gear with a 10-tooth gear in the STEP
// assembly. Two acceleration stages therefore produce a 4:1 speed increase.
export const MD01_SPEED_MULTIPLIER = 4
export const MD01_TORQUE_MULTIPLIER = 1 / MD01_SPEED_MULTIPLIER
export const SIMULATION_MAX_RPM = 180
export const SCENE_UNITS_PER_METER = 20
export const C103_MOTOR_LIMITS = {
  cube: Math.PI * 0.49,
  reverseCube: -Math.PI * 0.58,
} as const
export const CUBE_MOTOR_MAX_TORQUE_NM = 0.054

export type MotorDirection = 'forward' | 'stop' | 'reverse'

export type MotorControl = {
  direction: MotorDirection
  speedPercent: number
}

export type MotorDrive = {
  cubeInstanceId: string
  drivingInstanceId: string
  drivingConnectorId: string
  drivenInstanceId: string
  drivenConnectorId: string
  connectionId: string
  speedMultiplier: number
  torqueMultiplier: number
  transmissionInstanceIds: string[]
}

export type PlanarWheelSample = {
  relativeX: number
  relativeZ: number
  velocityX: number
  velocityZ: number
}

export type SpatialVector = {
  x: number
  y: number
  z: number
}

export const DEFAULT_MOTOR_CONTROL: MotorControl = {
  direction: 'stop',
  speedPercent: 20,
}

export function orderedCubes(parts: PlacedPart[]) {
  return parts.filter((part) => part.partId === PINGPONG_CUBE_PART_ID)
}

export function motorControlFor(
  controls: Record<string, MotorControl>,
  cubeInstanceId: string,
): MotorControl {
  return controls[cubeInstanceId] ?? DEFAULT_MOTOR_CONTROL
}

export function motorDirectionSign(direction: MotorDirection) {
  if (direction === 'forward') return 1
  if (direction === 'reverse') return -1
  return 0
}

export function motorRpm(control: MotorControl) {
  return Math.round(
    SIMULATION_MAX_RPM
    * Math.min(100, Math.max(0, control.speedPercent))
    / 100,
  )
}

export function motorOutputRpm(control: MotorControl, drive: MotorDrive) {
  return Math.round(motorRpm(control) * drive.speedMultiplier)
}

export function motorRadiansPerSecond(
  control: MotorControl,
  speedMultiplier = 1,
) {
  return motorDirectionSign(control.direction)
    * SIMULATION_MAX_RPM
    * Math.min(100, Math.max(0, control.speedPercent))
    / 100
    * speedMultiplier
    * Math.PI * 2
    / 60
}

export function constrainDrivenMotorAngle(
  partId: string,
  candidateAngle: number,
) {
  if (partId !== 'part-link-c1-c1-03') {
    return {
      angle: candidateAngle % (Math.PI * 2),
      blockedBy: null as 'floor' | 'cube' | null,
    }
  }
  if (candidateAngle > C103_MOTOR_LIMITS.cube) {
    return { angle: C103_MOTOR_LIMITS.cube, blockedBy: 'cube' as const }
  }
  if (candidateAngle < C103_MOTOR_LIMITS.reverseCube) {
    return { angle: C103_MOTOR_LIMITS.reverseCube, blockedBy: 'cube' as const }
  }
  return { angle: candidateAngle, blockedBy: null }
}

export function motorTorqueSceneUnits(control: MotorControl) {
  const power = Math.min(100, Math.max(0, control.speedPercent)) / 100
  return CUBE_MOTOR_MAX_TORQUE_NM * SCENE_UNITS_PER_METER ** 2 * power
}

export function revolutePivotOffset(
  pivot: SpatialVector,
  axis: SpatialVector,
  angle: number,
): SpatialVector {
  const axisLength = Math.hypot(axis.x, axis.y, axis.z)
  if (axisLength < 1e-8) return { x: 0, y: 0, z: 0 }
  const unitAxis = {
    x: axis.x / axisLength,
    y: axis.y / axisLength,
    z: axis.z / axisLength,
  }
  const cosine = Math.cos(angle)
  const sine = Math.sin(angle)
  const dot = unitAxis.x * pivot.x + unitAxis.y * pivot.y + unitAxis.z * pivot.z
  const cross = {
    x: unitAxis.y * pivot.z - unitAxis.z * pivot.y,
    y: unitAxis.z * pivot.x - unitAxis.x * pivot.z,
    z: unitAxis.x * pivot.y - unitAxis.y * pivot.x,
  }
  const rotatedPivot = {
    x: pivot.x * cosine + cross.x * sine + unitAxis.x * dot * (1 - cosine),
    y: pivot.y * cosine + cross.y * sine + unitAxis.y * dot * (1 - cosine),
    z: pivot.z * cosine + cross.z * sine + unitAxis.z * dot * (1 - cosine),
  }

  return {
    x: pivot.x - rotatedPivot.x,
    y: pivot.y - rotatedPivot.y,
    z: pivot.z - rotatedPivot.z,
  }
}

export function solvePlanarDrive(samples: PlanarWheelSample[]) {
  if (!samples.length) return { velocityX: 0, velocityZ: 0, angularVelocity: 0 }

  const count = samples.length
  const mean = samples.reduce((result, sample) => ({
    relativeX: result.relativeX + sample.relativeX / count,
    relativeZ: result.relativeZ + sample.relativeZ / count,
    velocityX: result.velocityX + sample.velocityX / count,
    velocityZ: result.velocityZ + sample.velocityZ / count,
  }), { relativeX: 0, relativeZ: 0, velocityX: 0, velocityZ: 0 })

  let angularNumerator = 0
  let angularDenominator = 0
  for (const sample of samples) {
    const offsetX = sample.relativeX - mean.relativeX
    const offsetZ = sample.relativeZ - mean.relativeZ
    angularNumerator += (sample.velocityX - mean.velocityX) * offsetZ
      - (sample.velocityZ - mean.velocityZ) * offsetX
    angularDenominator += offsetX ** 2 + offsetZ ** 2
  }
  const angularVelocity = angularDenominator > 1e-8
    ? angularNumerator / angularDenominator
    : 0

  return {
    velocityX: mean.velocityX - angularVelocity * mean.relativeZ,
    velocityZ: mean.velocityZ + angularVelocity * mean.relativeX,
    angularVelocity,
  }
}

export function findMotorDrives(
  parts: PlacedPart[],
  connections: AssemblyConnection[],
): MotorDrive[] {
  const partsByInstanceId = new Map(parts.map((part) => [part.instanceId, part]))
  const drives: MotorDrive[] = []

  const counterpart = (
    connection: AssemblyConnection,
    instanceId: string,
    connectorId: string,
  ) => {
    if (
      connection.fromPartId === instanceId
      && connection.fromConnectorId === connectorId
    ) {
      return {
        instanceId: connection.toPartId,
        connectorId: connection.toConnectorId,
      }
    }
    if (
      connection.toPartId === instanceId
      && connection.toConnectorId === connectorId
    ) {
      return {
        instanceId: connection.fromPartId,
        connectorId: connection.fromConnectorId,
      }
    }
    return null
  }

  for (const cube of orderedCubes(parts)) {
    const inputConnection = connections.find((connection) =>
      counterpart(connection, cube.instanceId, 'motor-female-front'))
    if (!inputConnection) continue
    const input = counterpart(
      inputConnection,
      cube.instanceId,
      'motor-female-front',
    )
    if (!input) continue
    const inputPart = partsByInstanceId.get(input.instanceId)
    if (!inputPart) continue

    if (
      inputPart.partId === MD01_PART_ID
      && input.connectorId === 'motor-male-input'
    ) {
      const outputConnection = connections.find((connection) =>
        connection.jointType === 'revolute'
        && counterpart(connection, inputPart.instanceId, 'motor-female-output'))
      if (!outputConnection) continue
      const output = counterpart(
        outputConnection,
        inputPart.instanceId,
        'motor-female-output',
      )
      if (!output || !partsByInstanceId.has(output.instanceId)) continue
      drives.push({
        cubeInstanceId: cube.instanceId,
        drivingInstanceId: inputPart.instanceId,
        drivingConnectorId: 'motor-female-output',
        drivenInstanceId: output.instanceId,
        drivenConnectorId: output.connectorId,
        connectionId: outputConnection.id,
        speedMultiplier: MD01_SPEED_MULTIPLIER,
        torqueMultiplier: MD01_TORQUE_MULTIPLIER,
        transmissionInstanceIds: [inputPart.instanceId],
      })
      continue
    }

    if (inputConnection.jointType !== 'revolute') continue
    drives.push({
      cubeInstanceId: cube.instanceId,
      drivingInstanceId: cube.instanceId,
      drivingConnectorId: 'motor-female-front',
      drivenInstanceId: input.instanceId,
      drivenConnectorId: input.connectorId,
      connectionId: inputConnection.id,
      speedMultiplier: 1,
      torqueMultiplier: 1,
      transmissionInstanceIds: [],
    })
  }

  return drives
}
